import omit from "lodash/omit";

import CachetLogo from "@/assets/images/integrations/cachet.png";
import DataDogLogo from "@/assets/images/integrations/datadog.jpeg";
import DefaultLogo from "@/assets/images/integrations/default.png";
import DiscordLogo from "@/assets/images/integrations/discord.png";
import GeckoBoardLogo from "@/assets/images/integrations/geckoboard.png";
import GoogleChatLogo from "@/assets/images/integrations/google_chat.png";
import GrafanaLogo from "@/assets/images/integrations/grafana.png";
import IlertLogo from "@/assets/images/integrations/ilert.jpeg";
import JiraLogo from "@/assets/images/integrations/jira.png";
import KlipfolioLogo from "@/assets/images/integrations/klipfolio.png";
import LogzLogo from "@/assets/images/integrations/logz.png";
import MakeLogo from "@/assets/images/integrations/make.png";
import MicrosoftTeamsLogo from "@/assets/images/integrations/ms_teams.png";
import OpsGenieLogo from "@/assets/images/integrations/opsgenie.png";
import PagerDutyLogo from "@/assets/images/integrations/pagerduty.png";
import PushBulletLogo from "@/assets/images/integrations/pushbullet.png";
import PushoverLogo from "@/assets/images/integrations/pushover.png";
import ServiceNowLogo from "@/assets/images/integrations/servicenow.png";
import SlackLogo from "@/assets/images/integrations/slack.png";
import SplunkLogo from "@/assets/images/integrations/splunk.png";
import StatusLogo from "@/assets/images/integrations/status.png";
import StatusPageLogo from "@/assets/images/integrations/statuspage.jpg";
import TelegramLogo from "@/assets/images/integrations/telegram.png";
import WaveFrontLogo from "@/assets/images/integrations/wavefront.png";
import WhatsAppLogo from "@/assets/images/integrations/whatsapp.png";
import ZapierLogo from "@/assets/images/integrations/zapier.png";
import ZendeskLogo from "@/assets/images/integrations/zendesk.png";
import {IntegrationFormData, IntegrationListData} from "@/lib/types/integrations.ts";
import {DataDogDefaults} from "@/pages/integrations/edit/subForms/dataDogInfo.ts";
import {GrafanaGraphiteDefaults} from "@/pages/integrations/edit/subForms/grafanaGraphiteInfo.ts";
import {GrafanaPrometheusDefaults} from "@/pages/integrations/edit/subForms/grafanaPrometheusInfo.ts";
import {JiraDefaults} from "@/pages/integrations/edit/subForms/jiraInfo.ts";
import {LogzDefaults} from "@/pages/integrations/edit/subForms/logzInfo.ts";
import {MakeDefaults} from "@/pages/integrations/edit/subForms/makeInfo.ts";
import {OpsGenieDefaults} from "@/pages/integrations/edit/subForms/opsGenieInfo.ts";
import {PushoverDefaults} from "@/pages/integrations/edit/subForms/pushoverInfo.ts";
import {ServiceNowDefaults} from "@/pages/integrations/edit/subForms/serviceNowInfo.ts";
import {WaveFrontDefaults} from "@/pages/integrations/edit/subForms/waveFrontInfo.ts";
import {WebhookDefaults} from "@/pages/integrations/edit/subForms/webhookInfo.ts";
import {WhatsAppDefaults} from "@/pages/integrations/edit/subForms/whatsAppInfo.ts";
import {ZendeskDefaults} from "@/pages/integrations/edit/subForms/zendeskInfo.ts";

// These need to match the python module name for the corresponding integration
export enum IntegrationModules {
  JIRA = "jiraservicedesk",
  CACHET = "cachet",
  WEBHOOK = "webhook",
  DATADOG = "datadog",
  DISCORD = "discord",
  GECKOBOARD = "geckoboard",
  GOOGLE_CHAT = "google_chat",
  GRAFANA_GRAPHITE = "grafana_graphite",
  GRAFANA_PROMETHEUS = "grafana_prometheus",
  ILERT = "ilert",
  KLIPFOLIO = "klipfolio",
  LOGZ = "logz",
  MAKE = "make",
  MS_TEAMS = "microsoft_teams",
  OPSGENIE = "opsgenie",
  PAGERDUTY = "pagerduty",
  PUSHBULLET = "pushbullet",
  PUSHOVER = "pushover",
  SERVICENOW = "servicenow",
  SLACK = "slack",
  SPLUNK = "victorops",
  STATUS = "status",
  STATUSPAGE = "statuspage",
  TELEGRAM = "telegram",
  WAVEFRONT = "wavefront",
  WHATSAPP = "whatsapp",
  ZAPIER = "zapier",
  ZENDESK = "zendesk",
}

export type IntegrationModuleInfo = {
  label: string;
  description?: string;
  url?: string;
  logo?: string;
  readonly?: boolean;
  popular?: boolean;
  defaults?: Record<string, unknown> | undefined;
};

export const IntegrationModuleInfo: Record<IntegrationModules, IntegrationModuleInfo> = {
  [IntegrationModules.CACHET]: {
    label: "Cachet",
    description: "Allow Uptime.com status changes to update component status on your Cachet page.",
    url: "https://cachethq.io",
    logo: CachetLogo,
  },
  [IntegrationModules.DATADOG]: {
    label: "Datadog",
    description: "Push Uptime.com alerts and response time metrics into Datadog.",
    url: "https://www.datadoghq.com",
    logo: DataDogLogo,
    defaults: DataDogDefaults,
  },
  [IntegrationModules.DISCORD]: {
    label: "Discord",
    description: "Display Uptime.com alerts and useful links within Discord.",
    url: "https://discord.com",
    logo: DiscordLogo,
  },
  [IntegrationModules.GECKOBOARD]: {
    label: "Geckoboard",
    description: "Push alerts from Uptime.com into Geckoboard.",
    url: "https://www.geckoboard.com",
    logo: GeckoBoardLogo,
  },
  [IntegrationModules.GOOGLE_CHAT]: {
    label: "Google Chat",
    description: "Send alerts to a Google Chat space for up/down events.",
    url: "https://chat.google.com",
    logo: GoogleChatLogo,
  },
  [IntegrationModules.GRAFANA_GRAPHITE]: {
    label: "Grafana Graphite",
    description: "Push Uptime.com response time metrics to Grafana Cloud via Graphite ingestion.",
    url: "https://grafana.com",
    logo: GrafanaLogo,
    defaults: GrafanaGraphiteDefaults,
  },
  [IntegrationModules.GRAFANA_PROMETHEUS]: {
    label: "Grafana Prometheus",
    description: "Push Uptime.com response time metrics to Grafana Cloud via Prometheus.",
    url: "https://grafana.com",
    logo: GrafanaLogo,
    defaults: GrafanaPrometheusDefaults,
  },
  [IntegrationModules.ILERT]: {
    label: "ilert",
    description: "Push Uptime.com alerts into ilert for incident management and on-call scheduling.",
    url: "https://www.ilert.com",
    logo: IlertLogo,
  },
  [IntegrationModules.JIRA]: {
    label: "Jira Service Desk",
    description: "Push Uptime.com alerts into Jira Service Desk.",
    url: "https://www.atlassian.com/software/jira/service-desk",
    logo: JiraLogo,
    defaults: JiraDefaults,
  },
  [IntegrationModules.KLIPFOLIO]: {
    label: "Klipfolio",
    description: "Send response time metrics from Uptime.com into Klipfolio.",
    url: "https://www.klipfolio.com",
    logo: KlipfolioLogo,
  },
  [IntegrationModules.LOGZ]: {
    label: "Logz.io",
    description:
      "Seamless collection and analysis of Uptime.com metrics related to your website's performance in Logz.io.",
    url: "https://logz.io",
    logo: LogzLogo,
    defaults: LogzDefaults,
  },
  [IntegrationModules.MAKE]: {
    label: "Make.com",
    description: "Trigger Make.com scenarios when alerts are raised or cleared.",
    url: "https://www.make.com",
    logo: MakeLogo,
    defaults: MakeDefaults,
  },
  [IntegrationModules.MS_TEAMS]: {
    label: "Microsoft Teams",
    description: "Push Uptime.com alerts into Microsoft Teams.",
    url: "https://teams.microsoft.com",
    logo: MicrosoftTeamsLogo,
    popular: true,
  },
  [IntegrationModules.OPSGENIE]: {
    label: "OpsGenie",
    description: "Push Uptime.com alerts to the Opsgenie platform.",
    url: "https://www.opsgenie.com",
    logo: OpsGenieLogo,
    popular: true,
    defaults: OpsGenieDefaults,
  },
  [IntegrationModules.PAGERDUTY]: {
    label: "PagerDuty",
    description: "Create and resolve incidents at PagerDuty.",
    url: "https://www.pagerduty.com",
    logo: PagerDutyLogo,
    popular: true,
  },
  [IntegrationModules.PUSHBULLET]: {
    label: "Pushbullet",
    description: "Push alerts from Uptime.com to your Pushbullet account.",
    url: "https://www.pushbullet.com",
    logo: PushBulletLogo,
  },
  [IntegrationModules.PUSHOVER]: {
    label: "Pushover",
    description: "Push alerts from Uptime.com into Pushover.",
    url: "https://pushover.net",
    logo: PushoverLogo,
    defaults: PushoverDefaults,
  },
  [IntegrationModules.SERVICENOW]: {
    label: "ServiceNow",
    description: "Create a ServiceNow incident when an alert is raised and resolve it when cleared.",
    url: "https://www.servicenow.com",
    logo: ServiceNowLogo,
    defaults: ServiceNowDefaults,
  },
  [IntegrationModules.SLACK]: {
    label: "Slack",
    description: "Send alerts to a Slack channel for up/down events.",
    url: "https://slack.com",
    logo: SlackLogo,
    popular: true,
  },
  [IntegrationModules.STATUS]: {
    label: "Status.io",
    description: "Push alerts and response time metrics to Status.io from Uptime.com.",
    url: "https://status.io/",
    logo: StatusLogo,
  },
  [IntegrationModules.STATUSPAGE]: {
    label: "StatusPage.io",
    description: "Push Uptime.com alerts and metrics to your StatusPage.io account.",
    url: "https://www.statuspage.io",
    logo: StatusPageLogo,
    popular: true,
  },
  [IntegrationModules.TELEGRAM]: {
    label: "Telegram",
    description: "Push alerts from Uptime.com into Telegram.",
    url: "https://telegram.org/",
    logo: TelegramLogo,
  },
  [IntegrationModules.SPLUNK]: {
    label: "Splunk On-Call",
    description: "Push alerts Uptime.com alerts to Splunk On-Call (formerly VictorOps)",
    url: "https://www.splunk.com",
    logo: SplunkLogo,
  },
  [IntegrationModules.WAVEFRONT]: {
    label: "Aria Operations for Applications",
    description:
      "Push alert events and response time metrics to Aria Operations for Applications (formerly Wavefront).",
    url: "https://tanzu.vmware.com/observability",
    logo: WaveFrontLogo,
    defaults: WaveFrontDefaults,
  },
  [IntegrationModules.WHATSAPP]: {
    label: "WhatsApp",
    description: "Send up/down Alerts to WhatsApp phone numbers.",
    url: "https://www.whatsapp.com/",
    logo: WhatsAppLogo,
    defaults: WhatsAppDefaults,
  },
  [IntegrationModules.WEBHOOK]: {
    label: "Webhook",
    description: "A webhook, or the Uptime.com Custom Postback URL, allows you to push alerts to a third party system.",
    popular: true,
    logo: DefaultLogo,
    defaults: WebhookDefaults,
  },
  [IntegrationModules.ZAPIER]: {
    label: "Zapier",
    description: "Send alerts to from Uptime.com to one of the over 8,000 other services connected to Zapier.",
    url: "https://zapier.com",
    logo: ZapierLogo,
    readonly: true,
  },
  [IntegrationModules.ZENDESK]: {
    label: "Zendesk",
    description: "Create a Zendesk ticket when an alert is raised and mark it solved when cleared.",
    url: "https://www.zendesk.com",
    logo: ZendeskLogo,
    defaults: ZendeskDefaults,
  },
};

const knownIntegrationModuleSet = new Set<string>(Object.keys(IntegrationModuleInfo));

export const isKnownIntegrationModule = (module: string | null | undefined): module is IntegrationModules =>
  !!module && knownIntegrationModuleSet.has(module);

export const getIntegrationModuleInfo = (module: string | null | undefined): IntegrationModuleInfo | undefined =>
  isKnownIntegrationModule(module) ? IntegrationModuleInfo[module] : undefined;

export const generateBlankIntegrationFormData = (module: IntegrationModules) => {
  return {
    module: module,
    name: IntegrationModuleInfo[module].label,
    contact_groups: [],
    settings: IntegrationModuleInfo[module]?.defaults ?? {},
    is_enabled: true,
  };
};

export const integrationListToForm = (
  integration: Omit<IntegrationListData, "errors"> & {errors?: IntegrationListData["errors"]}
): IntegrationFormData => {
  const integrationFormData = omit(integration, ["errors"]);

  const contactGroupIds = integration.contact_groups.map((cg) => cg.id);

  return {...integrationFormData, contact_groups: contactGroupIds};
};
